import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Promotion } from '../models/Promotion';
import promotionService from '../services/promotionService';
import CategoryProductSelector from '../components/CategoryProductSelector';
import CustomerPhoneSelector from '../components/CustomerPhoneSelector';
import {
  Typography,
  Box,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Stack,
  Chip
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TodayIcon from '@mui/icons-material/Today';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useAdminScope } from '../contexts/AdminScopeContext';
import ScopeRequirementAlert from '../components/ScopeRequirementAlert';

interface PromotionFormData {
  id: string;
  active: boolean;
  description: string;
  discountType: 'fixed' | 'percentage' | 'free_delivery';
  discountValue: number | string;
  applyOn: 'subtotal' | 'subtotal_plus_delivery';
  freeDelivery: boolean;
  autoApplyInStepClient: boolean;
  startDate: Date;
  endDate: Date;
  usageLimitPerUser: number | string;
  usageLimitTotal: number | string;
  minOrderAmount: number | string;
  eligibleProducts: string[];
  eligiblePhones: string[]; // numéros clients éligibles (E.164)
}

const Promotions: React.FC = () => {
  const { activeScopeType, selectedStoreId } = useAdminScope();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPromo, setEditingPromo] = useState<Promotion | null>(null);
  const [formData, setFormData] = useState<PromotionFormData>({
    id: '',
    active: true,
    description: '',
    discountType: 'fixed',
    discountValue: '',
    applyOn: 'subtotal',
    freeDelivery: false,
    autoApplyInStepClient: false,
    startDate: new Date(),
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // +7 jours par défaut
    usageLimitPerUser: '',
    usageLimitTotal: '',
    minOrderAmount: '',
    eligibleProducts: [],
    eligiblePhones: []
  });

  // Charger les promotions
  useEffect(() => {
    if (activeScopeType !== 'store' || !selectedStoreId) {
      setPromotions([]);
      setLoading(false);
      return;
    }

    const loadPromotions = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await promotionService.getPromotions();
        setPromotions(data);
      } catch {
        setError('Erreur lors du chargement des codes promo');
      } finally {
        setLoading(false);
      }
    };
    loadPromotions();
  }, [activeScopeType, selectedStoreId]);


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAdd = () => {
    setEditingPromo(null);
    setFormData({
      id: '',
      active: true,
      description: '',
      discountType: 'fixed',
      discountValue: '',
      applyOn: 'subtotal',
      freeDelivery: false,
      autoApplyInStepClient: false,
      startDate: new Date(),
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // +7 jours par défaut
      usageLimitPerUser: '',
      usageLimitTotal: '',
      minOrderAmount: '',
      eligibleProducts: [],
      eligiblePhones: []
    });
    setOpenDialog(true);
  };

  const handleEdit = (promo: Promotion) => {
    setEditingPromo(promo);
    setFormData({
      id: promo.id,
      active: promo.active,
      description: promo.description,
      discountType: promo.discountType,
      discountValue: promo.discountValue ?? '',
      applyOn: (promo as any).applyOn ?? 'subtotal',
      freeDelivery: (promo as any).freeDelivery ?? false,
      autoApplyInStepClient: promo.autoApplyInStepClient === true,
      startDate: new Date(promo.startDate),
      endDate: new Date(promo.endDate),
      usageLimitPerUser: promo.usageLimitPerUser ?? '',
      usageLimitTotal: promo.usageLimitTotal ?? '',
      minOrderAmount: promo.minOrderAmount ?? '',
      eligibleProducts: promo.eligibleProducts ?? [],
      eligiblePhones: promo.eligiblePhones ?? []
    });
    setOpenDialog(true);
  };

  // Fonctions utilitaires pour les dates
  const setStartDateToday = () => {
    setFormData(prev => ({ ...prev, startDate: new Date() }));
  };

  const setStartDateTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setFormData(prev => ({ ...prev, startDate: tomorrow }));
  };

  const setEndDateInOneWeek = () => {
    const oneWeek = new Date(formData.startDate);
    oneWeek.setDate(oneWeek.getDate() + 7);
    setFormData(prev => ({ ...prev, endDate: oneWeek }));
  };

  const setEndDateInOneMonth = () => {
    const oneMonth = new Date(formData.startDate);
    oneMonth.setMonth(oneMonth.getMonth() + 1);
    setFormData(prev => ({ ...prev, endDate: oneMonth }));
  };

  const handleSubmit = async () => {
    try {
      const payload: any = {
        active: formData.active,
        description: formData.description,
        discountType: formData.discountType,
        discountValue:
          formData.discountType === 'free_delivery'
            ? undefined
            : Number(formData.discountValue),
        // Nouveaux champs pour fixed/percentage
        applyOn:
          formData.discountType === 'free_delivery'
            ? undefined
            : (formData.applyOn || 'subtotal'),
        freeDelivery:
          formData.discountType === 'free_delivery'
            ? undefined
            : Boolean(formData.freeDelivery),
        autoApplyInStepClient: Boolean(formData.autoApplyInStepClient),
        startDate: formData.startDate,
        endDate: formData.endDate,
        usageLimitPerUser:
          formData.usageLimitPerUser === ''
            ? undefined
            : Number(formData.usageLimitPerUser),
        usageLimitTotal:
          formData.usageLimitTotal === ''
            ? undefined
            : Number(formData.usageLimitTotal),
        minOrderAmount:
          formData.minOrderAmount === ''
            ? undefined
            : Number(formData.minOrderAmount),
        eligibleProducts:
          formData.eligibleProducts.length > 0
            ? formData.eligibleProducts
            : undefined,
        eligiblePhones:
          formData.eligiblePhones.length > 0
            ? formData.eligiblePhones
            : undefined
      };

      if (editingPromo) {
        await promotionService.updatePromotion(editingPromo.id, payload);
      } else {
        await promotionService.createPromotion(formData.id, payload);
      }
      setOpenDialog(false);
      // Refresh promotions
      const data = await promotionService.getPromotions();
      setPromotions(data);
    } catch {
      setError('Erreur lors de la sauvegarde du code promo');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(`Supprimer le code promo "${id}" ?`)) {
      try {
        await promotionService.deletePromotion(id);
        const data = await promotionService.getPromotions();
        setPromotions(data);
      } catch {
        setError('Erreur lors de la suppression');
      }
    }
  };

  const handleToggleActive = async (promo: Promotion) => {
    try {
      await promotionService.updatePromotion(promo.id, { active: !promo.active });
      const data = await promotionService.getPromotions();
      setPromotions(data);
    } catch {
      setError('Erreur lors de la mise à jour du statut');
    }
  };

  const handleToggleAutoApply = async (promo: Promotion) => {
    try {
      await promotionService.updatePromotion(promo.id, {
        autoApplyInStepClient: !promo.autoApplyInStepClient
      });
      const data = await promotionService.getPromotions();
      setPromotions(data);
    } catch {
      setError("Erreur lors de la mise à jour de l'application automatique");
    }
  };

  return (
    <Layout title="Gestion des Codes Promo">
      {activeScopeType !== 'store' || !selectedStoreId ? (
        <ScopeRequirementAlert
          requiredScope="store"
          title="Contexte magasin requis"
          description="Les promotions actuelles sont rattachées au catalogue du magasin. Sélectionne un scope Magasin depuis la barre haute."
        />
      ) : (
        <>
      <Box mb={2} display="flex" justifyContent="space-between">
        <Typography variant="h4">Codes Promo</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          Ajouter
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Valeur</TableCell>
                <TableCell>Période</TableCell>
                <TableCell>Actif</TableCell>
                <TableCell>Auto Step Client</TableCell>
                <TableCell>Clients</TableCell>
                <TableCell>Utilisations</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {promotions.map(p => {
                const clientsLabel = p.eligiblePhones && p.eligiblePhones.length > 0
                  ? `${p.eligiblePhones.length} client(s)`
                  : 'Tous';
                return (
                  <TableRow key={p.id}>
                    <TableCell>{p.id}</TableCell>
                    <TableCell>{p.description}</TableCell>
                    <TableCell>{p.discountType}</TableCell>
                    <TableCell>
                      {p.discountType === 'free_delivery' ? '-' : p.discountValue}
                    </TableCell>
                    <TableCell>
                      {new Date(p.startDate).toLocaleString()} –{' '}
                      {new Date(p.endDate).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Switch checked={p.active} onChange={() => handleToggleActive(p)} />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={p.autoApplyInStepClient === true}
                        onChange={() => handleToggleAutoApply(p)}
                      />
                    </TableCell>
                    <TableCell>{clientsLabel}</TableCell>
                    <TableCell>
                      {p.usedCountTotal}
                      {p.usageLimitTotal ? ` / ${p.usageLimitTotal}` : ''}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleEdit(p)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(p.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
        <Dialog 
          open={openDialog} 
          onClose={() => setOpenDialog(false)} 
          maxWidth="lg" 
          fullWidth
          fullScreen={isMobile}
          scroll="paper"
        >
          <DialogTitle>
            {editingPromo ? `Modifier "${editingPromo.id}"` : 'Nouveau Code Promo'}
          </DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            <Stack spacing={3} sx={{ mt: 1 }}>
              {!editingPromo && (
                <TextField
                  name="id"
                  label="Code"
                  value={formData.id}
                  onChange={handleInputChange}
                  required
                  fullWidth
                />
              )}
              <TextField
                name="description"
                label="Description"
                value={formData.description}
                onChange={handleInputChange}
                required
                fullWidth
                multiline
                rows={2}
              />
              <TextField
                name="discountType"
                label="Type de réduction"
                select
                SelectProps={{ native: true }}
                value={formData.discountType}
                onChange={handleInputChange}
                fullWidth
              >
                <option value="fixed">Montant fixe</option>
                <option value="percentage">Pourcentage</option>
                <option value="free_delivery">Livraison offerte</option>
              </TextField>
              {formData.discountType !== 'free_delivery' && (
                <>
                  <TextField
                    name="discountValue"
                    label={
                      formData.discountType === 'percentage'
                        ? 'Pourcentage de réduction'
                        : 'Valeur de la réduction'
                    }
                    type="number"
                    value={formData.discountValue}
                    onChange={handleInputChange}
                    fullWidth
                    helperText={
                      formData.discountType === 'percentage'
                        ? 'Saisir 15 pour 15%.'
                        : undefined
                    }
                  />
                  <TextField
                    name="applyOn"
                    label="Appliquer sur"
                    select
                    SelectProps={{ native: true }}
                    value={formData.applyOn}
                    onChange={handleInputChange}
                    fullWidth
                    helperText="Base de calcul de la remise"
                    sx={{ mt: 2 }}
                  >
                    <option value="subtotal">Sous-total (produits)</option>
                    <option value="subtotal_plus_delivery">Sous-total + Livraison</option>
                  </TextField>
                  <FormControlLabel
                    control={
                      <Switch
                        name="freeDelivery"
                        checked={formData.freeDelivery}
                        onChange={handleInputChange}
                      />
                    }
                    label="Livraison gratuite (en plus de la remise)"
                  />
                </>
              )}

              {/* Section des dates améliorée */}
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                  📅 Période de validité
                </Typography>
                
                {/* Raccourcis pour date de début */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Raccourcis date de début :
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                      icon={<TodayIcon />}
                      label="Aujourd'hui"
                      onClick={setStartDateToday}
                      variant="outlined"
                      size="small"
                    />
                    <Chip
                      icon={<CalendarMonthIcon />}
                      label="Demain"
                      onClick={setStartDateTomorrow}
                      variant="outlined"
                      size="small"
                    />
                  </Stack>
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <DateTimePicker
                    label="Date et heure de début"
                    value={formData.startDate}
                    onChange={(newValue) => {
                      if (newValue) {
                        setFormData(prev => ({ ...prev, startDate: newValue }));
                      }
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true
                      },
                      popper: {
                        sx: {
                          '& .MuiPickersLayout-root': {
                            fontSize: '1.1rem'
                          }
                        }
                      }
                    }}
                    format="dd/MM/yyyy HH:mm"
                  />
                  
                  <DateTimePicker
                    label="Date et heure de fin"
                    value={formData.endDate}
                    onChange={(newValue) => {
                      if (newValue) {
                        setFormData(prev => ({ ...prev, endDate: newValue }));
                      }
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true
                      },
                      popper: {
                        sx: {
                          '& .MuiPickersLayout-root': {
                            fontSize: '1.1rem'
                          }
                        }
                      }
                    }}
                    format="dd/MM/yyyy HH:mm"
                    minDateTime={formData.startDate}
                  />
                </Stack>

                {/* Raccourcis pour date de fin */}
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Raccourcis date de fin :
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                      icon={<AccessTimeIcon />}
                      label="Dans 1 semaine"
                      onClick={setEndDateInOneWeek}
                      variant="outlined"
                      size="small"
                    />
                    <Chip
                      icon={<CalendarMonthIcon />}
                      label="Dans 1 mois"
                      onClick={setEndDateInOneMonth}
                      variant="outlined"
                      size="small"
                    />
                  </Stack>
                </Box>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  name="usageLimitPerUser"
                  label="Limite par utilisateur"
                  type="number"
                  value={formData.usageLimitPerUser}
                  onChange={handleInputChange}
                  fullWidth
                  helperText="Laisser vide pour aucune limite"
                />
                <TextField
                  name="usageLimitTotal"
                  label="Limite totale"
                  type="number"
                  value={formData.usageLimitTotal}
                  onChange={handleInputChange}
                  fullWidth
                  helperText="Laisser vide pour aucune limite"
                />
              </Stack>
              
              <TextField
                name="minOrderAmount"
                label="Montant minimum commande (MAD)"
                type="number"
                value={formData.minOrderAmount}
                onChange={handleInputChange}
                fullWidth
                helperText="Laisser vide pour aucun minimum"
              />
              
              {/* Section produits éligibles */}
              <Box sx={{ my: 2 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    🛍️ Produits éligibles
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ mb: 2 }}
                  >
                    Sélectionnez les produits auxquels cette promotion s'applique. Laissez vide pour tous les produits.
                  </Typography>
                  <CategoryProductSelector
                    selectedProductIds={formData.eligibleProducts}
                    onSelectionChange={(selectedIds) =>
                      setFormData(prev => ({ ...prev, eligibleProducts: selectedIds }))}
                  />
                </Paper>
              </Box>

              {/* Section clients éligibles - carte distincte */}
              <Box sx={{ my: 2 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    👥 Clients éligibles
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Laissez vide pour rendre le code accessible à tous les clients. Sinon, sélectionnez un ou plusieurs clients par numéro de téléphone.
                  </Typography>
                  <CustomerPhoneSelector
                    selectedPhones={formData.eligiblePhones}
                    onChange={(phones) =>
                      setFormData(prev => ({ ...prev, eligiblePhones: phones }))
                    }
                  />
                </Paper>
              </Box>
              
              <FormControlLabel
                control={
                  <Switch
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                  />
                }
                label="Promotion active"
              />
              <FormControlLabel
                control={
                  <Switch
                    name="autoApplyInStepClient"
                    checked={formData.autoApplyInStepClient}
                    onChange={handleInputChange}
                  />
                }
                label="Appliquer automatiquement dans Step Client"
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 1 }}>
            <Button onClick={() => setOpenDialog(false)} size="large">
              Annuler
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              disabled={!editingPromo && !formData.id}
              size="large"
            >
              {editingPromo ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
        </>
      )}
    </Layout>
  );
};

export default Promotions;
